// components/TimesheetPieChartDashboard.tsx
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Stack,
} from "@mui/material";
import ApexCharts from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useTheme } from "@mui/material/styles";

import { apiGetTimesheetStats } from "../../api/dashboard";
import { useAuth } from "../../hooks/useAuth";
import { useNotificationService } from "../notification";

import { MonthOption, TimesheetData } from "../../interface/dashboard";
import {
  formatHours,
  generateMonthOptions,
  calculateTimesheetStats,
} from "../../utils/timesheets";
import { boolean } from "zod";
import useResponsive from "../../hooks/useResponsive";
import DashboardAbsentChart from "./dashboardAbsentChart";

// Dummy data for fallback
const dummyTimesheetData: TimesheetData[] = [
  {
    totalHours: 8,
    overtimeHours: 2,
    count: 1,
    date: new Date().toISOString().split("T")[0],
    medic: "No Data",
  },
];

type ChartView = "medics" | "hours" | "overtime";

const TimesheetPieChartDashboard: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { showNotification } = useNotificationService();
  const chartRef = useRef<ApexCharts | null>(null);
  const errorShownRef = useRef(false);

  const monthOptions = useMemo(() => generateMonthOptions(), []);
  const [selectedMonth, setSelectedMonth] = useState<MonthOption>(
    monthOptions[0],
  );
  const isSmall = useResponsive("down", "sm", "xl");
  const [chartView, setChartView] = useState<ChartView>("medics");
  const [retryCount, setRetryCount] = useState(0);

  // Memoize the query function to prevent unnecessary re-renders
  const queryFn = useCallback(
    () =>
      apiGetTimesheetStats(
        selectedMonth.startDate,
        selectedMonth.endDate,
        user?.facilityId ?? "",
      ),
    [selectedMonth.startDate, selectedMonth.endDate, user?.facilityId],
  );

  const { data, isLoading, isError, error, refetch } = useQuery<
    TimesheetData[]
  >({
    queryKey: [
      "timesheet-stats",
      selectedMonth.startDate,
      selectedMonth.endDate,
      user?.facilityId,
    ],
    queryFn,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    enabled:
      !!selectedMonth.startDate &&
      !!selectedMonth.endDate &&
      Boolean(user?.facilityId),
    // Remove placeholderData to avoid fallback to dummy data
    placeholderData: undefined,

    retry: (failureCount, error) => {
      // Limit retries to prevent infinite loops
      if (failureCount >= 3) return false;

      // Don't retry on 4xx errors (client errors)
      if (error && "status" in error) {
        const status = (error as any).status;
        if (status >= 400 && status < 500) return false;
      }

      return true;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Handle error state with proper cleanup
  useEffect(() => {
    if (isError && !errorShownRef.current) {
      errorShownRef.current = true;
      showNotification("Failed to fetch timesheet data", "error");

      // Reset error flag after some time
      const timer = setTimeout(() => {
        errorShownRef.current = false;
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [isError, showNotification]);

  // Cleanup ApexCharts on unmount
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        // chartRef.current.destroy();
      }
    };
  }, []);

  const handleMonthChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const selectedValue = event.target.value;
      const selectedOption = monthOptions.find(
        (option) => option.value === selectedValue,
      );
      if (selectedOption) {
        setSelectedMonth(selectedOption);
        setRetryCount(0);
        errorShownRef.current = false;
      }
    },
    [monthOptions],
  );

  const handleChartViewChange = useCallback(
    (event: React.MouseEvent<HTMLElement>, newView: ChartView) => {
      if (newView !== null) {
        setChartView(newView);
      }
    },
    [],
  );

  const handleRetry = useCallback(() => {
    setRetryCount((prev) => prev + 1);
    errorShownRef.current = false;
    refetch();
  }, [refetch]);

  // Process the actual data from the API
  const stats = useMemo(() => {
    // If still loading or no data, return empty array
    if (isLoading || !data || !Array.isArray(data)) {
      return [];
    }

    // Validate and clean the data
    const validData = data.filter(
      (item) =>
        item &&
        typeof item === "object" &&
        typeof item.totalHours === "number" &&
        typeof item.overtimeHours === "number" &&
        typeof item.medic === "string" &&
        !isNaN(item.totalHours) &&
        !isNaN(item.overtimeHours),
    );

    console.log("Processed stats:", validData); // Debug log
    return validData;
  }, [data, isLoading]);

  const aggregatedStats = useMemo(() => {
    if (stats.length === 0) {
      return {
        totalHours: 0,
        totalOvertimeHours: 0,
        totalCount: 0,
        averageHoursPerDay: 0,
      };
    }

    try {
      return calculateTimesheetStats(stats);
    } catch (error) {
      console.error("Error calculating stats:", error);
      return {
        totalHours: 0,
        totalOvertimeHours: 0,
        totalCount: 0,
        averageHoursPerDay: 0,
      };
    }
  }, [stats]);

  // Memoize chart data preparation
  const chartData = useMemo(() => {
    // Handle empty data state
    if (!stats || stats.length === 0) {
      return {
        series: [1],
        labels: ["No Data Available"],
        title: "No Data Available",
      };
    }

    try {
      switch (chartView) {
        case "medics":
          const medicHours = stats.reduce(
            (acc, item) => {
              const medic = item.medic || "Unknown";
              const hours = item.totalHours || 0;
              acc[medic] = (acc[medic] || 0) + hours;
              return acc;
            },
            {} as Record<string, number>,
          );

          // Filter out medics with 0 hours
          const filteredMedicHours = Object.entries(medicHours)
            .filter(([_, hours]) => hours > 0)
            .reduce(
              (acc, [medic, hours]) => {
                acc[medic] = hours;
                return acc;
              },
              {} as Record<string, number>,
            );

          const medicSeries = Object.values(filteredMedicHours).map((hours) =>
            parseFloat(formatHours(hours)),
          );

          return {
            series: medicSeries.length > 0 ? medicSeries : [1],
            labels:
              Object.keys(filteredMedicHours).length > 0
                ? Object.keys(filteredMedicHours)
                : ["No Data"],
            title: "Total Hours by Medic",
          };

        case "hours":
          const totalHours = aggregatedStats.totalHours;
          const totalOvertime = aggregatedStats.totalOvertimeHours;
          const regularHours = Math.max(0, totalHours - totalOvertime);

          // If no hours data, show placeholder
          if (totalHours === 0) {
            return {
              series: [1],
              labels: ["No Hours Data"],
              title: "Regular vs Overtime Hours",
            };
          }

          return {
            series: [
              parseFloat(formatHours(regularHours)),
              parseFloat(formatHours(totalOvertime)),
            ],
            labels: ["Regular Hours", "Overtime Hours"],
            title: "Regular vs Overtime Hours",
          };

        case "overtime":
          const medicOvertime = stats.reduce(
            (acc, item) => {
              const medic = item.medic || "Unknown";
              const overtime = item.overtimeHours || 0;
              if (overtime > 0) {
                // Only include medics with overtime
                acc[medic] = (acc[medic] || 0) + overtime;
              }
              return acc;
            },
            {} as Record<string, number>,
          );

          const overtimeSeries = Object.values(medicOvertime).map((hours) =>
            parseFloat(formatHours(hours)),
          );

          return {
            series: overtimeSeries.length > 0 ? overtimeSeries : [1],
            labels:
              Object.keys(medicOvertime).length > 0
                ? Object.keys(medicOvertime)
                : ["No Overtime Data"],
            title: "Overtime Hours by Medic",
          };

        default:
          return {
            series: [1],
            labels: ["No Data"],
            title: "No Data Available",
          };
      }
    } catch (error) {
      console.error("Error preparing chart data:", error);
      return {
        series: [1],
        labels: ["Error Loading Data"],
        title: "Data Processing Error",
      };
    }
  }, [chartView, stats, aggregatedStats]);

  // Memoize chart options
  const chartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "pie",
        toolbar: {
          show: false,
        },
        animations: {
          enabled: !isLoading,
        },
      },
      labels: chartData.labels,
      title: {
        text: chartData.title,
        align: "center",
        style: {
          color: theme.palette.text.primary,
          fontSize: "16px",
          fontWeight: "600",
        },
      },
      colors: [
        "#2F66F4",
        theme.palette.secondary.main,
        theme.palette.error.main,
        theme.palette.warning.main,
        theme.palette.info.main,
        theme.palette.success.main,
        "#9C27B0",
        "#FF9800",
        "#795548",
        "#607D8B",
      ],
      dataLabels: {
        enabled: true,
        formatter: function (val: number) {
          // Don't show percentage for "No Data" states
          if (
            chartData.labels.includes("No Data Available") ||
            chartData.labels.includes("No Hours Data") ||
            chartData.labels.includes("No Overtime Data") ||
            chartData.labels.includes("Error Loading Data")
          ) {
            return "";
          }
          return val.toFixed(1) + "%";
        },
        style: {
          colors: ["#fff"],
        },
      },
      legend: {
        position: "bottom",
        labels: {
          colors: theme.palette.text.secondary,
        },
        markers: {
          shape: "square",
        },
      },
      // tooltip: {
      //   theme: theme.palette.mode,
      //   y: {
      //     formatter: function (val: number) {
      //       // Don't show hours for "No Data" states
      //       if (chartData.labels.includes("No Data Available") ||
      //           chartData.labels.includes("No Hours Data") ||
      //           chartData.labels.includes("No Overtime Data") ||
      //           chartData.labels.includes("Error Loading Data")) {
      //         return "No data available";
      //       }
      //       return formatHours(val) + " hours";
      //     },
      //   },
      // },
      tooltip: {
        theme: theme.palette.mode,
        style: {
          fontSize: "12px",
          color: "#ffffff", // Force white text color
        },
        y: {
          formatter: function (val: number) {
            // Don't show hours for "No Data" states
            if (
              chartData.labels.includes("No Data Available") ||
              chartData.labels.includes("No Hours Data") ||
              chartData.labels.includes("No Overtime Data") ||
              chartData.labels.includes("Error Loading Data")
            ) {
              return "No data available";
            }
            return formatHours(val) + " hours";
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 200,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
      noData: {
        text: "No data available",
        align: "center",
        verticalAlign: "middle",
        style: {
          color: theme.palette.text.secondary,
        },
      },
    }),
    [chartData, theme, isLoading],
  );

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Controls */}
      <Box mb={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="month-select-label">Select Month</InputLabel>
              <Select
                labelId="month-select-label"
                value={selectedMonth.value}
                onChange={handleMonthChange}
                label="Select Month"
              >
                {monthOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={8}>
            <Box display="flex" gap={2} alignItems="center">
              <ToggleButtonGroup
                value={chartView}
                exclusive
                onChange={handleChartViewChange}
                aria-label="chart view"
                size="small"
              >
                <ToggleButton value="medics" aria-label="medics">
                  By Medics
                </ToggleButton>
                <ToggleButton value="hours" aria-label="hours">
                  Hours Split
                </ToggleButton>
                <ToggleButton value="overtime" aria-label="overtime">
                  Overtime
                </ToggleButton>
              </ToggleButtonGroup>

              {isError && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRetry}
                  disabled={isLoading}
                >
                  Retry
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Error Alert */}
      {isError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              Retry
            </Button>
          }
        >
          Failed to load timesheet data.{" "}
          {retryCount > 0 && `Retry attempt: ${retryCount}`}
        </Alert>
      )}

      {/* Data Status Info */}
      {!isLoading && !isError && stats.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No timesheet data found for {selectedMonth.label}. Please select a
          different month or check if data exists for this period.
        </Alert>
      )}

      {!isLoading && !isError && stats.length > 0 && (
        <Alert severity="success" sx={{ mb: 2, color: "black" }}>
          Loaded {stats.length} timesheet record{stats.length !== 1 ? "s" : ""}{" "}
          for {selectedMonth.label}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ py: 1, px: 3 }}>
              <Typography variant="caption" color="primary">
                Total Hours
              </Typography>
              <Typography variant="body2">
                {formatHours(aggregatedStats.totalHours)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ py: 1, px: 3 }}>
              <Typography variant="caption" color="secondary">
                Overtime Hours
              </Typography>
              <Typography variant="body2">
                {formatHours(aggregatedStats.totalOvertimeHours)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ py: 1, px: 3 }}>
              <Typography variant="caption">Total Records</Typography>
              <Typography variant="body2">
                {aggregatedStats.totalCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ py: 1, px: 3 }}>
              <Typography variant="caption">Avg Hours/Day</Typography>
              <Typography variant="body2">
                {formatHours(aggregatedStats.averageHoursPerDay)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pie Chart & Absentee */}
      <Card
        sx={{
          display: "flex",
          flexDirection: isSmall ? "column" : "row",
          alignItems: "center",
        }}
      >
        <CardContent sx={{ width: isSmall ? "94%" : "100%" }}>
          <Typography variant="body2" gutterBottom>
            Timesheet Analysis - {selectedMonth.label}
          </Typography>
          <Box display="flex" justifyContent="center">
            <ApexCharts
              ref={chartRef}
              options={chartOptions}
              series={chartData.series}
              type="pie"
              height={400}
              width="100%"
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TimesheetPieChartDashboard;
